/**
 * Pure helpers behind the org form's Sign-in card: what the consent badge
 * says, which mistakes to point out before a save, and how a failed
 * consent-link request reads. No React here so each rule is unit tested.
 */
import type { OrgSignin } from "@hub/lib/org-types";

export type ConsentView =
  | { kind: "not-applicable"; reason: string }
  | { kind: "pending"; tenantId: string }
  | { kind: "granted"; tenantId: string; grantedAt: string }
  | { kind: "failed"; code: string; at: string; tenantId: string; previouslyGranted: boolean };

export function consentView(signin: OrgSignin): ConsentView {
  if (signin.provider !== "microsoft") {
    return { kind: "not-applicable", reason: "Consent applies to Microsoft 365 organizations" };
  }
  if (signin.tenantId === null) {
    return { kind: "not-applicable", reason: "Record the tenant id first" };
  }
  const tenantId = signin.tenantId;
  if (signin.consentError) {
    return {
      kind: "failed",
      code: signin.consentError.code,
      at: signin.consentError.at,
      tenantId,
      previouslyGranted: Boolean(signin.consent),
    };
  }
  if (signin.consent) {
    return { kind: "granted", tenantId, grantedAt: signin.consent.grantedAt };
  }
  return { kind: "pending", tenantId };
}

/** The Client id field belongs to an org's own app only; the shared app ignores it. */
export function showsClientId(signin: OrgSignin): boolean {
  return signin.app === "own";
}

/** What the form sends: a shared-app org never carries a client id. */
export function signinForSave(signin: OrgSignin): OrgSignin {
  return signin.app === "own" ? signin : { ...signin, clientId: null };
}

export function signinHints(
  signin: OrgSignin,
  slug: string | null,
  nymblTenantId: string | null,
): string[] {
  const hints: string[] = [];
  const brokered = slug !== "nymbl";
  const tenant = signin.tenantId?.trim().toLowerCase() || null;
  if (signin.provider === "microsoft" && brokered) {
    if (tenant === null) {
      hints.push(
        "A tenant id is required: this organization's people sign in with Microsoft on the Helm customer app.",
      );
    } else if (nymblTenantId !== null && tenant === nymblTenantId.toLowerCase()) {
      hints.push("This is Nymbl's own tenant; another organization cannot use it.");
    }
  }
  if (signin.app === "own" && !signin.clientId?.trim()) {
    hints.push("Client id is required for the company's own app.");
  }
  if (signin.app === "own" && signin.provider === "microsoft") {
    hints.push(
      "Set the app's client secret below; it is stored encrypted on the hub, never shown again, and never placed on a box.",
      "Switching between the Helm customer app and an own app after people have signed in breaks their sign-in and Outlook; see the operations doc.",
    );
  }
  return hints;
}

export function consentLinkError(status: number, message: string): string {
  if (status === 409 && message === "the customer app is not configured on the hub yet") {
    return "Consent links are available once the Helm customer app is configured on the hub (phase 6, Task 8).";
  }
  return message;
}
